import {
  Badge,
  Box,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import actions from "src/actions";
import { KydEngageCampaignSummary } from "@common/interfaces/KydEngageCampaign";
import type { KYDScope } from "@common/interfaces/ICognito";

const CAN_SHOW_ADS_SCOPE: KYDScope = "events/campaigns:write";

const statusColorScheme: Record<string, string> = {
  ACTIVE: "green",
  SCHEDULED: "blue",
  PAUSED: "orange",
  COMPLETED: "gray",
  DRAFT: "purple",
  ARCHIVED: "gray",
  CANCELLED: "red",
  DELETED: "red",
  UNKNOWN: "gray",
};

const formatDateTime = (value?: string) =>
  value ? dayjs(value).format("MMM D, YYYY h:mmA") : "-";

const sortCampaignsByUpdatedAt = (
  campaigns: KydEngageCampaignSummary[]
): KydEngageCampaignSummary[] =>
  campaigns.sort((a, b) => {
    const left = new Date(
      a.updated_at || a.created_at || a.schedule?.start_at || 0
    ).getTime();
    const right = new Date(
      b.updated_at || b.created_at || b.schedule?.start_at || 0
    ).getTime();
    return right - left;
  });

export interface EngageOverviewProps {
  userScopes?: KYDScope[];
}

const EngageOverview = ({ userScopes }: EngageOverviewProps) => {
  const [campaigns, setCampaigns] = useState<KydEngageCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const includeAds = Boolean(
    userScopes?.includes(CAN_SHOW_ADS_SCOPE)
  );

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await actions.ADMIN.fetchEngageCampaigns(
        includeAds ? undefined : "EMAIL"
      );
      const list = Array.isArray(response?.campaigns)
        ? response.campaigns
        : [];
      setCampaigns(sortCampaignsByUpdatedAt([...list]));
    } catch (error) {
      console.error("Error fetching engage campaigns:", error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [includeAds]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const hasCampaigns = campaigns.length > 0;
  const emptyState = useMemo(
    () => (
      <VStack w="100%" py={6}>
        <Text color="gray.500">No campaigns found.</Text>
      </VStack>
    ),
    []
  );

  return (
    <VStack w="100%" alignItems="flex-start" spacing={6} py={4}>
      <HStack justifyContent="space-between" w="100%">
        <Text fontSize="32px" fontWeight="400">
          Engage Overview
        </Text>
        <Text fontSize="sm" color="gray.500">
          {includeAds ? "All campaigns (ads + email)" : "Email campaigns"}
        </Text>
      </HStack>

      {loading ? (
        <VStack w="100%" py={6}>
          <Spinner />
        </VStack>
      ) : !hasCampaigns ? (
        emptyState
      ) : (
        <Box w="100%" overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th px={0}>Name</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Channel</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Budget</Th>
                <Th>Spend</Th>
                <Th>Revenue</Th>
                <Th>ROAS</Th>
                <Th>CTR</Th>
                <Th>Tickets</Th>
              </Tr>
            </Thead>
            <Tbody>
              {campaigns.map((campaign) => (
                <Tr key={campaign.id}>
                  <Td px={0}>
                    <VStack align="flex-start" spacing={1}>
                      <Text fontWeight="600">{campaign.name}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {campaign.id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge variant="subtle">{campaign.campaign_type}</Badge>
                  </Td>
                  <Td>
                    <Badge
                      variant="subtle"
                      colorScheme={
                        statusColorScheme[campaign.status] || "gray"
                      }
                    >
                      {campaign.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge variant="outline">{campaign.channel}</Badge>
                  </Td>
                  <Td>{formatDateTime(campaign.schedule?.start_at)}</Td>
                  <Td>{formatDateTime(campaign.schedule?.end_at)}</Td>
                  <Td>{campaign.budget?.display_amount || "-"}</Td>
                  <Td>{campaign.metrics_summary?.display?.spend || "-"}</Td>
                  <Td>{campaign.metrics_summary?.display?.revenue || "-"}</Td>
                  <Td>{campaign.metrics_summary?.display?.roas || "-"}</Td>
                  <Td>{campaign.metrics_summary?.display?.ctr || "-"}</Td>
                  <Td>{campaign.metrics_summary?.display?.tickets || "-"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  );
};

export default EngageOverview;
